import { css, StyleSheet } from 'aphrodite';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { LabeledInput } from '../../../admin/components/LabeledInput/LabeledInput';
import { Column, Row } from '../../../admin/components/Layout';
import {
	extractFolderName,
	extractTypedFolderName,
	isTypingFolderKeyword,
} from '../../containers/Campaigns/folderUtils';
import { Button } from '../../components-v2/Button';
import { Pill } from '../../components-v2/Pill/Pill';
import { aceGray, brandPrimary, brandPrimaryActive, brandPrimaryHover, charmGray } from '../../styles/colors';
import { bs } from '../../styles/styles';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../../components-v2/Popover';
import { FolderIcon } from '../svgs/icons/FolderIcon';
import { PlusIcon } from '../svgs/icons/PlusIcon';

/**
 * Helper to get folder display info for a keyword.
 * Extracts folder name and returns appropriate display label and icon.
 */
const getFolderDisplayInfo = (keyword: string) => {
	const folderName = extractFolderName(keyword);
	const isFolder = folderName !== null;
	return {
		isFolder,
		displayLabel: isFolder ? folderName : keyword,
		icon: isFolder ? <FolderIcon fillColor='currentColor' /> : undefined,
	};
};

export interface PillMultiSelectProps {
	accessory?: React.ReactNode;
	addButtonLabel: string;
	disabled?: boolean;
	onEditOptions?: (options: string) => void;
	onOptionClick?: (option: string) => void;
	onRemove?: (option: string) => void;
	options: string[];
	readOnly?: boolean;
	selectedOptions?: string[];
	variant?: 'keyword';
}

/**
 * @deprecated in favor of components-v2/PillMultiSelect
 */
export const PillMultiSelect: FC<PillMultiSelectProps> = ({
	accessory,
	addButtonLabel = 'Add',
	disabled,
	onEditOptions,
	onOptionClick,
	onRemove,
	options,
	readOnly,
	selectedOptions,
	variant,
}) => {
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const trimmedInputValue = inputValue.trim();

	const handleOpen = () => {
		setOpen(true);
	};
	const handleClose = (event?: any) => {
		if (!inputRef.current?.contains(event?.target) && !buttonRef.current?.contains(event?.target)) {
			setInputValue('');
			setOpen(false);
		}
	};

	const handleAdd = (value: string) => {
		onOptionClick?.(value);
		// Clear input and close popover after adding
		setInputValue('');
		setOpen(false);
	};
	const handleRemove = (value: string) => {
		onRemove?.(value);
	};
	const handleEdit = () => {
		onEditOptions?.(inputValue);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};

	// Focus the input when popover opens
	useEffect(() => {
		if (open && inputRef.current) {
			// Small delay to ensure DOM is fully rendered
			setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
		}
	}, [open]);

	return (
		<Row className={css(styleSheet.container)}>
			{accessory && <span className={css(bs.pr2)}>{accessory}</span>}
			<Column className={css(bs.gap2)}>
				{selectedOptions.length > 0 && (
					<Row className={css(bs.gap2, bs.flexWrap)}>
						{selectedOptions?.map((selectedOption, i) => {
							const { displayLabel, icon } = getFolderDisplayInfo(selectedOption);
							return (
								<Pill
									variant={variant}
									label={displayLabel}
									value={selectedOption}
									key={`${i}-${selectedOption}`}
									onRemove={readOnly ? undefined : handleRemove}
									icon={icon}
								/>
							);
						})}
					</Row>
				)}
				{!readOnly && (
					<Popover open={open}>
						{!open ? (
							<PopoverTrigger className={css(styleSheet.linkButton)} onClick={handleOpen} disabled={disabled}>
								<PlusIcon />
								<span className={css(styleSheet.buttonLabel)}>{addButtonLabel}</span>
							</PopoverTrigger>
						) : (
							<PopoverAnchor className={css(styleSheet.inputRow)}>
								<LabeledInput
									variant='small'
									value={inputValue}
									onChange={handleInputChange}
									placeholder='Filter or add new'
									ref={inputRef}
								/>
								{onEditOptions && (
									<Button disabled={disabled} size='sm' onClick={handleEdit} ref={buttonRef}>
										Save
									</Button>
								)}
							</PopoverAnchor>
						)}
						<PopoverContent
							className={css(styleSheet.popoverContent)}
							onEscapeKeyDown={handleClose}
							onPointerDownOutside={handleClose}
						>
							{open ? (
								<PillMultiSelectPopoverContent
									options={options}
									selectedOptions={selectedOptions}
									value={trimmedInputValue}
									onAdd={handleAdd}
								/>
							) : null}
						</PopoverContent>
					</Popover>
				)}
			</Column>
		</Row>
	);
};

const PillMultiSelectPopoverContent = ({
	options,
	selectedOptions,
	value,
	onAdd,
}: {
	options: string[];
	selectedOptions?: string[];
	value: string;
	onAdd: (value: string) => void;
}) => {
	const filteredOptions = useMemo(() => {
		if (!options) {
			return [];
		}
		const selectedOptionsSet = new Set(selectedOptions);
		const filtered = [];
		for (const option of options) {
			if (selectedOptionsSet.has(option)) {
				continue;
			}
			if (!value || option.toLowerCase().includes(value.toLowerCase())) {
				filtered.push(option);
			}
		}
		return filtered;
	}, [selectedOptions, options, value]);

	// Check if user is typing a folder keyword (triggers on "folder" without needing colon)
	const isTypingFolder = isTypingFolderKeyword(value);
	const typedFolderName = extractTypedFolderName(value);

	// Check if we have a valid "Add New" option to show
	// For folders: only show when they've typed a folder name (not just "folder")
	// For keywords: show when any non-empty value is typed
	const hasValidAddOption = isTypingFolder ? !!typedFolderName : !!value;

	// Determine the "Add New" label based on what's being typed
	const getAddNewLabel = () => {
		if (isTypingFolder && typedFolderName) {
			return `Add New Folder: ${typedFolderName}`;
		}
		return `Add New Keyword: ${value}`;
	};

	// Get the actual value to save when adding
	const getValueToAdd = () => {
		if (isTypingFolder && typedFolderName) {
			// Format as proper folder keyword using the validated folder name
			return `folder: ${typedFolderName}`;
		}
		return value;
	};

	return (
		<div className={css(bs.flex, bs.wFull, bs.hFull, bs.flexWrap, bs.gap2)}>
			{filteredOptions.length > 0 ? (
				filteredOptions.map((option, i) => {
					const { displayLabel, icon } = getFolderDisplayInfo(option);
					return (
						<Pill
							variant='keyword'
							label={displayLabel}
							value={option}
							key={`${i}-${option}`}
							onClick={onAdd}
							icon={icon}
						/>
					);
				})
			) : hasValidAddOption ? (
				<Pill
					variant='keyword'
					label={getAddNewLabel()}
					value={getValueToAdd()}
					onClick={onAdd}
					icon={isTypingFolder ? <FolderIcon fillColor='currentColor' /> : undefined}
				/>
			) : null}
		</div>
	);
};

const styleSheet = StyleSheet.create({
	container: {
		alignItems: 'flex-start',
		display: 'flex',
		gap: 4,
	},
	iconButton: {
		padding: '4px 8px',
		backgroundColor: brandPrimary,
		maxHeight: 'fit-content',
		borderRadius: 4,
		':hover': {
			backgroundColor: brandPrimaryHover,
		},
		':active': {
			backgroundColor: brandPrimaryActive,
		},
	},
	linkButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		maxWidth: 'fit-content',
		padding: '4px 0px',
		gap: 4,
	},
	buttonLabel: {
		color: brandPrimary,
	},
	popoverContent: {
		maxWidth: 450,
		maxHeight: '50vh',
		overflowY: 'scroll',
		borderRadius: 8,
		'::-webkit-scrollbar': {
			background: 'transparent',
			width: 9,
		},
		'::-webkit-scrollbar-thumb': {
			borderRadius: 8,
			backgroundColor: aceGray,
			boxShadow: '0 0 1px rgba(255, 255, 255, .5)',
		},
	},
	inputRow: {
		display: 'flex',
		flexWrap: 'nowrap',
		flexDirection: 'row',
		gap: 4,
	},
	infoText: {
		color: charmGray,
		fontSize: 14,
	},
});
